import { useMemo, useState } from 'react';

import { Link } from '@tanstack/react-router';
import { CameraOffIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { env } from '@/constants/env';
import { cn } from '@/lib/utils';

type MediaCategory = {
  key: string;
  label: string;
  count: number;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  route?: string;
};

type EstateImageProps = {
  thumbnailId?: string | null;
  isRounded?: boolean;
  className?: string;
  iconSize?: number;
  url?: string;
  mediaCategories?: MediaCategory[];
  thumbnailSize?: 'thumbnail' | 'thumbnail200' | 'thumbnail500' | 'original';
};

export const EstateImage = ({
  thumbnailId = 'default-estate-thumbnail',
  isRounded,
  className,
  iconSize = 35,
  url,
  mediaCategories = [],
  thumbnailSize = 'thumbnail500',
}: EstateImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const imgSrc = useMemo(() => {
    if (url) return url;
    if (!thumbnailId) return null;
    const thumbnailUrl = new URL(`${env.API_URL}/images/${thumbnailId}`);
    if (thumbnailSize != null && thumbnailSize !== 'original')
      thumbnailUrl.searchParams.set('size', thumbnailSize);
    return thumbnailUrl.toString();
  }, [url, thumbnailId, thumbnailSize]);

  return (
    <div className={cn('relative aspect-square w-full', className)}>
      {!loaded && (
        <div
          className={cn(
            'flex size-full items-center justify-center overflow-hidden bg-secondary text-primary-light',
            isRounded && 'rounded-md'
          )}
        >
          <CameraOffIcon size={iconSize} />
        </div>
      )}

      <img
        className={cn('size-full object-cover', isRounded && 'rounded-md', !loaded && 'hidden')}
        src={imgSrc}
        {...(thumbnailId && { alt: thumbnailId })}
        onLoad={() => setLoaded(true)}
        alt="Estate preview"
      />

      {mediaCategories.length > 0 && (
        <div className="absolute bottom-4 left-4 z-20 flex flex-wrap gap-3">
          {mediaCategories
            .filter((cat) => cat.count > 0)
            .map((cat) => {
              const Icon = cat.icon;
              const contentInner = (
                <div className="flex h-10 w-auto flex-row items-center justify-center gap-2 rounded-md bg-white/55 px-3 py-2 shadow-md backdrop-blur-sm group-focus-visible:ring-2 group-focus-visible:ring-primary-dark group-focus-visible:ring-offset-2">
                  <Icon aria-hidden="true" className="h-5 w-5 text-teal-900" strokeWidth={2.5} />
                  <span className="text-base font-bold text-teal-900">{cat.count}</span>
                </div>
              );
              const accessibleLabel = `${cat.label}: ${cat.count}`;
              const clickable = cat.route ? (
                <Link
                  to={cat.route}
                  preload="intent"
                  aria-label={accessibleLabel}
                  title={cat.label}
                  className="group focus:outline-none"
                >
                  {contentInner}
                </Link>
              ) : (
                <div>{contentInner}</div>
              );
              return (
                <Tooltip key={cat.key}>
                  <TooltipTrigger asChild>{clickable}</TooltipTrigger>
                  <TooltipContent className="border bg-white text-black shadow">
                    {cat.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
        </div>
      )}
    </div>
  );
};
